import { Input } from './input.mjs';
import { registerCustomElement } from '../utils.mjs';
import RemoteForm from '../controls/remoteFormPanel.mjs';
import { createFileIcon } from './file-list';
import classes from './file-storage.module.scss';
import { html, nothing, render } from 'lit-html';
import { ref, createRef } from 'lit-html/directives/ref.js';
import type { Panel } from '../controls/panel.mjs';
import common from '../common.module.scss';
import Loader from './../loader.mjs';
import FormanConfig from '../formanConfig.mjs';
import { I18n } from '../helpers/i18n.mjs';
import EVENTS from '../events.mjs';

const config = FormanConfig.config.fields.filestorage;

const DEFAULT_ACCEPTED_FILE_TYPES = 'text/plain,.txt,application/pdf,.pdf';

type FileStorageApiData = {
	createdAt: Date;
	description: string;
	documentName: string;
	id: string;
	teamId: string;
	url: string;
};

type FileStorageApiResponse = {
	data: Array<FileStorageApiData>;
	usage?: {
		consumedCredits: number;
	};
};

type FileStoragePostApiResponse = {
	data: FileStorageApiData;
	usage?: {
		consumedCredits: number;
	};
};

type FileStorageFile = {
	id: string;
	documentName: string;
	description: string;
	type: string;
	url: string;
	spentCenticredits?: number;
};

type FileStorageInputInstructions = {
	description?: string;
	upload?: {
		title?: string;
		buttonLabel?: string;
		accept?: string;
		supportedFileTypesLabel?: string;
		maxFileSizeMB?: number;
		submitLabel?: string;
		multiple?: boolean;
		additionalDescription?: string;
	};
	select?: {
		title?: string;
		buttonLabel?: string;
		submitLabel?: string;
		searchLabel?: string;
	};
	value?: Array<string>;
};

export class FileStorageInput extends Input {
	public override get allowApiCall(): boolean {
		return true;
	}

	public get teamId(): number {
		return this.form?.meta?.teamId || 0;
	}

	public override value: Array<string> = [];

	openedPanel?: Panel = undefined;
	openedEditPanel?: RemoteForm = undefined;
	storeFiles?: Array<FileStorageFile> = [];

	async _build(instructions: FileStorageInputInstructions, value: Array<string> | null = null) {
		this._instructions = instructions;
		this.value = value ?? [];

		this.render(true);

		this.storeFiles = (await this.listFiles()) || [];
		this.render();
	}

	private render(isLoading: boolean = false) {
		const instructions = this._instructions as FileStorageInputInstructions;
		const files = this.value.map((id) => this.storeFiles?.find((file) => file.id === id)).filter((file) => !!file);
		const template = html`
			<div class="${classes.description}">${instructions.description}</div>
			<div>
				<div class=${classes.actionButtons}>
					${instructions.upload
						? html`<button
								@click=${(event: Event) => this.openUploadPanel(event, instructions)}
								class="btn btn-sm btn-outline-secondary"
						  >
								<i class="fa-regular fa-arrow-up-from-bracket dmo-mr-2"></i>
								${instructions.upload.buttonLabel || I18n.l('fileStorage.upload.buttonLabel')}
						  </button>`
						: nothing}
					<button
						@click=${(event: Event) => this.openSelectPanel(event, instructions)}
						class="btn btn-sm btn-outline-secondary"
					>
						<i class="fa-regular fa-magnifying-glass dmo-mr-2"></i>
						${instructions.select?.buttonLabel || I18n.l('fileStorage.select.buttonLabel')}
					</button>
				</div>
				${isLoading ? html`<div class="${classes.loadingItems}"></div>` : nothing}
				<ul class="${classes.list}">
					${files.map((file) =>
						this.createFileItem(file, {
							remove: true,
							link: true,
							edit: true,
							onEdit: (e) => {
								this.openEditPanel(e, file);
							},
							onRemove: () => {
								this.value = this.value.filter((id) => id !== file.id);
								this.render();
							},
						}),
					)}
				</ul>
			</div>
		`;

		render(template, this);
	}

	async openEditPanel(event: Event, file: FileStorageFile) {
		if (this.openedEditPanel) {
			return;
		}

		this.openedEditPanel = new RemoteForm(event.target);

		this.openedEditPanel.addEventListener(EVENTS.FORM.SUBMITTED, async (event: Event) => {
			const customEvent = event as CustomEvent<{ value: { id: string; description: string } }>;
			const value = customEvent.detail.value;

			const updatedFile = await this.patchFile(value);
			if (!updatedFile || !this.storeFiles) {
				return;
			}

			const fileIndex = this.storeFiles.findIndex((f) => f.id === updatedFile.id);
			if (fileIndex === -1) {
				return;
			}

			this.storeFiles[fileIndex] = updatedFile;
			this.render();
		});

		this.openedEditPanel.addEventListener(EVENTS.PANEL.CLOSE, () => {
			this.openedEditPanel = undefined;
		});

		await this.openedEditPanel.build(
			{
				name: I18n.l('fileStorage.editDescription'),
				config: [
					{
						type: 'hidden',
						name: 'id',
						default: file.id,
					},
					{
						type: 'text',
						label: I18n.l('fileStorage.description'),
						name: 'description',
						multiline: true,
						rows: 4,
						required: true,
						default: file.description || '',
					},
				],
				buttons: [
					{
						label: '{{!common.save}}',
						action: 'submit',
					},
				],
			},
			{ relative: event.target },
		);

		this.openedEditPanel.open();
	}

	openUploadPanel(event: Event, instructions?: FileStorageInputInstructions) {
		if (this.openedPanel) {
			this.openedPanel.close();
		}

		const panel = this.getPanel(event.target, instructions?.upload?.title || 'Upload knowledge files');
		this.openedPanel = panel;

		const uploadWrapperRef = createRef<HTMLDivElement>();

		const onFileInputChange = async (e: Event) => {
			const input = e.target as HTMLInputElement;
			const files = Array.from(input.files || []);
			input.value = '';

			const getFileIcon = (uploadedFile: FileStorageFile | undefined, uploadError: boolean) => {
				if (uploadedFile) {
					return createFileIcon(uploadedFile.type);
				}
				if (uploadError) {
					return html`<i class="ml-1 fa-solid fa-hexagon-exclamation" style="color: red;"></i>`;
				}
				return html`<i class="ml-1 far fa-circle-notch fa-spin"></i>`;
			};

			files.forEach(async (file) => {
				const uploadFileRow = uploadWrapperRef.value!.appendChild(document.createElement('div'));
				uploadFileRow.className = classes.uploadedFile;

				if (file) {
					const uploadFileTemplate = ({
						uploadedFile = undefined,
						uploadError = false,
					}: {
						uploadedFile?: FileStorageFile;
						uploadError?: boolean;
					}) => {
						return html`<div class="${classes.uploadedFileLoader}" data-file-id="${uploadedFile?.id || nothing}">
								${getFileIcon(uploadedFile, uploadError)}
							</div>
							<div class="${classes.uploadedFileLabel}">
								<label>${file.name}</label>
								<small>${this.formatBytes(file.size)}</small>
							</div>
							<div class="${classes.uploadedFileActions}">
								${uploadedFile?.spentCenticredits && uploadedFile?.spentCenticredits > 0
									? this.getConsumedBadge(uploadedFile?.spentCenticredits)
									: nothing}
								${uploadError
									? html`<button
											class="${common.button} ${common.buttonSmall} ${common.buttonGhost}"
											@click=${async () => {
												const fileInput = panel.body.querySelector<HTMLInputElement>('input[type="file"]')!;

												const dt = new DataTransfer();
												dt.items.add(file);
												fileInput.files = dt.files;
												uploadFileRow.remove();
												onFileInputChange({ target: fileInput } as unknown as Event);
											}}
									  >
											<i class="fa-solid fa-arrows-rotate"></i>
									  </button>`
									: nothing}
								<span class=${classes.itemButtonsDivider}></span>
								<button
									class="${common.button} ${common.buttonSmall} ${common.buttonGhost}"
									@click=${async () => {
										if (uploadError) {
											uploadFileRow.remove();
											return;
										}

										if (await this.deleteFile(uploadedFile!)) {
											uploadFileRow.remove();
										}
									}}
								>
									<i class="far fa-trash-alt"></i>
								</button>
							</div> `;
					};

					render(uploadFileTemplate({}), uploadFileRow);

					const data = new FormData();
					data.append('file', file);

					let uploadedFile: FileStorageFile;
					try {
						await this.createFile(data).then(async (file) => {
							if (file) {
								uploadedFile = file;
								this.storeFiles?.push(uploadedFile);
								render(uploadFileTemplate({ uploadedFile }), uploadFileRow);
							} else {
								render(uploadFileTemplate({ uploadError: true }), uploadFileRow);
							}
						});
					} catch (error) {
						if (error && typeof error === 'object' && 'code' in error && error.code === 413) {
							panel.warn(I18n.l('fileStorage.limitExceeded', { name: file.name }));
						} else {
							panel.warn(error);
						}

						console.error('Error uploading file:', error);
						render(uploadFileTemplate({ uploadError: true }), uploadFileRow);
					}
				}
			});
		};

		const isSupportedFileType = (file: { type: string; name?: string }) => {
			const acceptedTypes = (instructions?.upload?.accept || DEFAULT_ACCEPTED_FILE_TYPES)
				.split(',')
				.map((type) => type.trim().toLowerCase());

			const fileType = file.type.toLowerCase();
			return (
				acceptedTypes.includes(fileType) ||
				acceptedTypes.some((type) => type.startsWith('.') && file.name?.toLowerCase().endsWith(type))
			);
		};

		const hasSupportedFileTypes = (items: Array<{ type: string; name?: string }> | undefined) => {
			if (!items) {
				return false;
			}

			for (const item of items) {
				if (isSupportedFileType(item)) {
					return true;
				}
			}

			return false;
		};

		const template = html`<div
				${ref(uploadWrapperRef)}
				class="${classes.uploadWrapper}"
				@drop=${(e: DragEvent) => {
					e.preventDefault();
					const files = Array.from(e.dataTransfer?.files || []).filter((file) => isSupportedFileType(file));
					const fileInput = panel.body.querySelector<HTMLInputElement>('input[type="file"]')!;
					if (files?.length) {
						const dt = new DataTransfer();
						for (const file of files) {
							dt.items.add(file);
						}
						fileInput.files = dt.files;
						onFileInputChange({ target: fileInput } as unknown as Event);
					}

					if (uploadWrapperRef.value) {
						uploadWrapperRef.value.classList.remove(classes.dragOver);
					}
				}}
				@dragover=${(e: DragEvent) => {
					if (uploadWrapperRef.value) {
						uploadWrapperRef.value.classList.add(classes.dragOver);
					}

					const supported = hasSupportedFileTypes(Array.from(e.dataTransfer?.items || []));
					e.dataTransfer!.dropEffect = supported ? 'copy' : 'none';
				}}
				@dragleave=${(e: DragEvent) => {
					if (uploadWrapperRef.value?.contains(e.relatedTarget as Node)) {
						return;
					}

					if (uploadWrapperRef.value) {
						uploadWrapperRef.value.classList.remove(classes.dragOver);
					}
				}}
			>
				<label class="${common.button} ${common.buttonSecondary}"
					><input
						type="file"
						name="file"
						accept="${instructions?.upload?.accept || DEFAULT_ACCEPTED_FILE_TYPES}"
						multiple=${instructions?.upload?.multiple || false}
						style="display: none;"
						@change=${onFileInputChange}
					/><i class="fa-regular fa-arrow-up-from-bracket"></i> Choose file </label
				><span class="${classes.supportedFileTypes}">
					${instructions?.upload?.supportedFileTypesLabel || I18n.l('fileStorage.upload.supportedFileTypesLabel')}
				</span>
			</div>
			<div class="mt-2 ${classes.description}">${instructions?.upload?.additionalDescription}</div>`;

		// render body
		render(template, panel.body as HTMLElement);

		const onAddClick = () => {
			const newFilesIds = Array.from(panel.body.querySelectorAll<HTMLDivElement>('[data-file-id]')).map((el) => {
				return el.dataset.fileId!;
			});

			this.value = this.value.concat(newFilesIds.filter((id) => !this.value.includes(id)));
			panel.close();
			this.render();
		};

		const footerButton = html`<credits-badge
				text="AI tokens"
				color="credits"
				hideHeader="true"
				tooltipDescription="Uploading files will consume AI tokens based on the file size."
				tooltipPosition="left"
				tooltipDelay="700"
			></credits-badge
			><button @click=${onAddClick} class="btn btn-primary btn-sm ml-auto">
				${instructions?.upload?.submitLabel || 'Add'}
			</button>`;
		render(footerButton, panel.footer as HTMLElement);

		panel.open();
	}

	async openSelectPanel(event: Event, instructions?: FileStorageInputInstructions) {
		if (this.openedPanel) {
			this.openedPanel.close();
		}

		const panel = this.getPanel(event.target, instructions?.select?.title || I18n.l('fileStorage.select.title'));
		this.openedPanel = panel;

		panel.open();
		panel.setLoading(true);

		this.storeFiles = (await this.listFiles()) || [];

		panel.setLoading(false);

		const onSearchInput = (e: Event) => {
			const input = e.target as HTMLInputElement;
			const searchTerm = input.value.toLowerCase();

			for (const li of panel.querySelectorAll<HTMLElement>('ul li')) {
				const fileName = li.textContent?.toLowerCase() || '';
				if (fileName.includes(searchTerm)) {
					li.style.display = '';
				} else {
					li.style.display = 'none';
				}
			}
		};

		const getTemplate = () =>
			html`<div>
				<input
					type="text"
					@input=${onSearchInput}
					placeholder="${instructions?.select?.searchLabel || I18n.l('fileStorage.select.searchLabel')}"
					class="form-control mb-2 mt-1"
				/>
				<div>
					<ul class="${classes.list} mb-4">
						${this.storeFiles
							?.filter((x: FileStorageFile) => !this.value.includes(x.id))
							.map((file: FileStorageFile) =>
								this.createFileItem(
									file,
									{
										select: true,
										link: true,
										edit: true,
										del: true,
										onEdit: (e: Event) => {
											this.openEditPanel(e, file);
										},
										onDelete: async () => {
											if (await this.deleteFile(file)) {
												this.storeFiles = this.storeFiles?.filter((f) => f.id !== file.id);
												render(getTemplate(), panel.body as HTMLElement);
											}
										},
									},
									classes.itemSelect,
								),
							)}
					</ul>
				</div>
			</div>`;

		// render body
		render(getTemplate(), panel.body as HTMLElement);

		const onAddClick = () => {
			const checkedFiles = Array.from(
				(panel.body as HTMLElement).querySelectorAll<HTMLInputElement>('input[name="file"]:checked'),
			);

			const selectedFileIds = checkedFiles.map((input) => input?.value);
			this.value = this.value.concat(selectedFileIds.filter((id) => !this.value.includes(id)));

			this.render();
			panel.close();
		};

		const footerButton = html` <button @click=${() => panel.close()} class="btn btn-sm btn-outline-secondary ml-auto">
				${I18n.l('fileStorage.cancel')}
			</button>
			<button @click=${onAddClick} class="btn btn-sm btn-primary ml-1">
				${instructions?.select?.submitLabel || I18n.l('fileStorage.select.submitLabel')}
			</button>`;
		render(footerButton, panel.footer as HTMLElement);
	}

	private getPanel(relative: EventTarget | null, title: string) {
		const panel = document.createElement('imt-panel') as Panel;

		panel.width = '440';
		panel.position = ['right', 'left'];
		panel.relative = relative;

		const panelHeader = panel.header as HTMLElement & { addCloseButton: () => void };
		const titleElement = document.createElement('h1');

		titleElement.innerText = title;
		panelHeader.append(titleElement);
		panelHeader.addCloseButton();

		return panel;
	}

	private createFileItem(
		file: FileStorageFile,
		{
			select = false,
			remove = false,
			del = false,
			edit = false,
			link = false,
			onEdit = () => {},
			onRemove = () => {},
			onDelete = async () => {},
		}: {
			select?: boolean;
			remove?: boolean;
			del?: boolean;
			edit?: boolean;
			link?: boolean;
			onEdit?: (e: Event) => void;
			onRemove?: (e: Event) => void;
			onDelete?: (e: Event) => Promise<void>;
		} = {},
		customClass: string = '',
	) {
		return html`<li class="${classes.item} ${customClass}">
			<label>
				${select
					? html`<input type="checkbox" name="file" value=${file.id} ?disabled=${this.value.includes(file.id)} />`
					: nothing}
				${createFileIcon(file.type)} <span class="${classes.itemLabelText}">${file.documentName}</span>
			</label>

			<div class="${classes.itemButtons}">
				${link
					? html`<a
							class="${common.button} ${common.buttonSmall} ${common.buttonGhost}"
							href="${file.url}"
							target="_blank"
							rel="noopener noreferrer"
							><i class="far fa-arrow-up-right-from-square"></i
					  ></a>`
					: nothing}
				${edit
					? html`<button
							class="${common.button} ${common.buttonSmall} ${common.buttonGhost}"
							@click=${(e: Event) => onEdit(e)}
					  >
							<i class="far fa-pencil"></i>
					  </button>`
					: nothing}
				${del
					? html` <span class=${classes.itemButtonsDivider}></span>
							<button
								class="${common.button} ${common.buttonSmall} ${common.buttonGhost}"
								@click=${(e: Event) => onDelete(e)}
							>
								<i class="far fa-trash-alt"></i>
							</button>`
					: nothing}
				${remove
					? html` <span class=${classes.itemButtonsDivider}></span>
							<button
								class="${common.button} ${common.buttonSmall} ${common.buttonGhost}"
								@click=${(e: Event) => onRemove(e)}
							>
								<i class="far fa-times dmo-text-base"></i>
							</button>`
					: nothing}
			</div>
		</li>`;
	}

	private async listFiles() {
		const storeQuery = new URLSearchParams();
		storeQuery.append('teamId', this.teamId.toString());

		try {
			const response: FileStorageApiResponse | null = await Loader.guardedLoad(
				`${config.api.list.path}?${storeQuery}`,
				{ method: 'GET' },
				this.allowApiCall,
			);

			if (response && response.data) {
				return response.data.map((file) => ({
					id: file.id,
					documentName: file.documentName,
					description: file.description,
					type: file.documentName.split('.').pop() || 'txt',
					url: file.url,
				}));
			}

			return undefined;
		} catch (error) {
			console.error('Error listing files:', error);

			return undefined;
		}
	}

	private async createFile(formData: FormData): Promise<FileStorageFile | undefined> {
		const storeQuery = new URLSearchParams();
		storeQuery.append('teamId', this.teamId.toString());

		const response: FileStoragePostApiResponse = await Loader.guardedLoad(
			`${config.api.create.path}?${storeQuery}`,
			{
				method: 'POST',
				body: formData,
			},
			this.allowApiCall,
		);

		return {
			id: response.data.id,
			documentName: response.data.documentName,
			description: response.data.description,
			type: response.data.documentName.split('.').pop() || 'txt',
			url: response.data.url,
			spentCenticredits: response.usage?.consumedCredits,
		};
	}

	private async patchFile(file: Partial<FileStorageFile>): Promise<FileStorageFile | undefined> {
		if (!file.id) {
			return undefined;
		}

		try {
			const response = await Loader.guardedLoad(
				config.api.edit.path,
				{
					method: 'PATCH',
					body: {
						description: file.description,
					},
					context: {
						fileId: file.id,
						teamId: this.teamId,
					},
				},
				this.allowApiCall,
			);
			const responseData = response?.data ?? response;
			if (!responseData?.documentName || !responseData?.id) {
				throw new Error('Invalid patch file response');
			}

			return {
				id: responseData.id,
				documentName: responseData.documentName,
				description: responseData.description,
				type: responseData.documentName.split('.').pop() || 'txt',
				url: responseData.url,
			};
		} catch (error) {
			console.error('Error patching file:', error);

			return undefined;
		}
	}

	private async deleteFile(file: FileStorageFile) {
		try {
			await Loader.guardedLoad(
				config.api.delete.path,
				{
					method: 'DELETE',
					body: {
						id: file.id,
					},
					context: {
						fileId: file.id,
					},
				},
				this.allowApiCall,
			);

			return true;
		} catch (error) {
			console.error('Error deleting file:', error);

			return undefined;
		}
	}

	private formatBytes(bytes: number) {
		if (bytes === 0) return '0 B';
		const k = 1024;
		const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];

		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
	}

	private getConsumedBadge(consumed: number) {
		return html` <div class="${classes.consumedBadgeWrapper}">
			<span class="${classes.consumedBadge}">
				<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g clip-path="url(#clip0_3983_19128)">
						<path
							d="M5.69336 2.65625C8.54207 2.65642 10.8505 4.96476 10.8506 7.81348C10.8504 10.6621 8.542 12.9715 5.69336 12.9717C2.84457 12.9717 0.535386 10.6622 0.535156 7.81348C0.535285 4.96466 2.84451 2.65625 5.69336 2.65625ZM9.84961 3.40137C10.3453 3.37165 10.8437 3.42616 11.3232 3.5625C11.9374 3.7372 12.5074 4.04243 12.9922 4.45801C13.4767 4.87352 13.8655 5.38965 14.1318 5.96973C14.3982 6.54999 14.536 7.18184 14.5352 7.82031C14.5343 8.45861 14.3956 9.08951 14.1279 9.66895C13.8601 10.2485 13.4692 10.7633 12.9834 11.1777C12.4977 11.5921 11.928 11.8971 11.3135 12.0703C10.8334 12.2056 10.3337 12.2575 9.83789 12.2266C9.60785 12.2119 9.45257 11.9963 9.48828 11.7686C9.52442 11.5404 9.73893 11.3872 9.96973 11.3965C10.3455 11.4116 10.7223 11.367 11.0859 11.2646C11.5841 11.1243 12.0466 10.8779 12.4404 10.542C12.8343 10.206 13.151 9.78829 13.3682 9.31836C13.5853 8.84851 13.6985 8.33692 13.6992 7.81934C13.6999 7.3018 13.5879 6.78971 13.3721 6.31934C13.1561 5.8489 12.8402 5.42974 12.4473 5.09277C12.0543 4.75599 11.5925 4.50879 11.0947 4.36719C10.7312 4.26382 10.3544 4.21819 9.97852 4.23242C9.74782 4.24117 9.53377 4.08741 9.49805 3.85938C9.46257 3.6312 9.61909 3.41521 9.84961 3.40137ZM5.69336 3.62305C3.37843 3.62305 1.50208 5.49858 1.50195 7.81348C1.50218 10.1283 3.3785 12.0049 5.69336 12.0049C8.00808 12.0047 9.88356 10.1282 9.88379 7.81348C9.88366 5.49868 8.00814 3.62321 5.69336 3.62305ZM5.28809 5.92578C5.51191 5.70231 5.87493 5.70212 6.09863 5.92578L7.58008 7.4082C7.80349 7.63204 7.80375 7.99507 7.58008 8.21875L6.09863 9.7002C5.87494 9.92366 5.51186 9.92353 5.28809 9.7002L3.80566 8.21875C3.58211 7.99514 3.58249 7.63205 3.80566 7.4082L5.28809 5.92578Z"
							fill="#7302D5"
						></path>
					</g>
					<defs>
						<clipPath id="clip0_3983_19128">
							<rect width="14" height="14" fill="white" transform="translate(0.535156 0.917969)"></rect>
						</clipPath>
					</defs>
				</svg>

				<span>${consumed / 100}</span>
			</span>
		</div>`;
	}
}

registerCustomElement('imt-input-filestorage', FileStorageInput);
